/**
 * "Before you start" facts for specialty tools: what it does, what you need,
 * how long it takes and its limits. Keep these honest and short.
 */
import { maturityFor, type Maturity } from "./toolTaxonomy";

export interface ToolFacts {
  name: string;
  does: string;
  needs: string[];
  time: string;
  limits: string[];
  /** How results are made: written by AI, fixed rules, or both. */
  method: "ai" | "rules" | "mixed";
  /** Tool takes file uploads; shows the privacy note. */
  uploads?: boolean;
  /** Shown when the tool can't fully work yet. */
  setupNote?: string;
}

export const TOOL_FACTS: Record<string, ToolFacts> = {
  "creative-director": {
    name: "Creative Director",
    does: "Turns a short brief into a full production plan: scenes, shots, characters and prompts.",
    needs: ["A one-paragraph idea", "Who it's for and the style you want"],
    time: "About 1 minute per plan",
    limits: ["Plans only, no finished video", "Real brand names are only used if you type them"],
    method: "ai",
  },
  "storyboard-video-studio": {
    name: "Storyboard Video Studio",
    does: "Builds a shot-by-shot storyboard and keeps characters and places consistent.",
    needs: ["A story idea or a Creative Director plan"],
    time: "2–5 minutes for a storyboard; video clips take longer",
    limits: ["Video clips depend on outside services and may queue", "Exports are for your own review"],
    method: "ai",
  },
  "character-replacement": {
    name: "Character Replacement Studio",
    does: "Swaps a person in a short video clip with a character you choose.",
    needs: ["A short video clip", "A clear photo of the new character"],
    time: "Several minutes per clip once available",
    limits: ["One person per clip", "Clips stay private to your account"],
    method: "ai", uploads: true,
    setupNote: "Video processing isn't switched on yet. You can look around and upload, but clips won't be processed until it's ready.",
  },
  "cyber-kitchen": {
    name: "CyberKitchen",
    does: "Encodes, decodes, hashes and transforms text and data step by step.",
    needs: ["Text or data to work on"],
    time: "Instant",
    limits: ["Runs in your browser; nothing is sent to us", "Very large inputs may be slow"],
    method: "rules",
  },
  "contract-risk-analyzer": {
    name: "Contract Risk Analyzer",
    does: "Reads a contract and flags risky clauses in plain English.",
    needs: ["A contract as PDF, Word or pasted text"],
    time: "1–3 minutes per contract",
    limits: ["Not legal advice", "Files are encrypted and visible only to you"],
    method: "ai", uploads: true,
  },
  "chatbot-builder": {
    name: "Chatbot Builder",
    does: "Creates a chatbot that answers from your own documents and web pages.",
    needs: ["Files (up to 5 MB each) or website links"],
    time: "A few minutes to set up",
    limits: ["Each source takes up to 15 seconds to read", "Deleting a bot removes its sources"],
    method: "ai", uploads: true,
  },
  "everyday-suite": {
    name: "Everyday Suite",
    does: "Small helpers for daily tasks: messages, plans, lists and more.",
    needs: ["A sentence about what you need"],
    time: "Under a minute",
    limits: ["Answers are AI-written; double-check important details"],
    method: "ai",
  },
};

export const factsFor = (id: string): (ToolFacts & { maturity: Maturity }) | null => {
  const f = TOOL_FACTS[id];
  return f ? { ...f, maturity: maturityFor(id) } : null;
};
